import { useEffect, useCallback } from "react";
import { useWebSocket } from "./useWebSocket";
import { toast } from "react-toastify";

// Order event payload types
export interface OrderEventPayload {
  orderId: number | string;
  trackingId?: string;
  storeId?: number;
  customerId?: number;
  deliveryBoyId?: number;
  totalAmount?: number;
  status?: string;
  timestamp?: string;
  notification?: {
    title?: string;
    body?: string;
    pushBody?: string;
  };
  [key: string]: any;
}

export interface UseOrderEventsOptions {
  onNewOrder?: (data: OrderEventPayload) => void;
  onOrderUpdated?: (data: OrderEventPayload) => void;
  onDeliveryAssigned?: (data: OrderEventPayload) => void;
  onStatusChange?: (data: OrderEventPayload) => void;
  playSound?: boolean;
  showToasts?: boolean;
}

// Sound utility using audio file
const playNotificationSound = (
  type: "newOrder" | "statusUpdate" = "newOrder",
) => {
  try {
    if (type === "newOrder") {
      // Play new order sound file
      const audio = new Audio("/neworderplaced.mpeg");
      audio.play().catch((e) => {
        console.warn("Failed to play new order sound:", e);
      });
    } else {
      // Single beep for status updates using Web Audio API
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioContextClass) return;

      const audioContext = new AudioContextClass();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(660, audioContext.currentTime); // E5
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        audioContext.currentTime + 0.2,
      );
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    }
  } catch (e) {
    console.warn("Failed to play notification sound:", e);
  }
};

export const useOrderEvents = (options: UseOrderEventsOptions = {}) => {
  const {
    onNewOrder,
    onOrderUpdated,
    onDeliveryAssigned,
    onStatusChange,
    playSound = true,
    showToasts = true,
  } = options;

  const { socket, connected } = useWebSocket();

  // New order handler
  const handleNewOrder = useCallback(
    (data: OrderEventPayload) => {
      console.log("🆕 New order received:", data);

      if (playSound) {
        playNotificationSound("newOrder");
      }

      if (showToasts) {
        const amount = data.totalAmount ? ` - Rs. ${data.totalAmount}` : "";
        toast.success(`🛒 New Order #${data.orderId}${amount}`, {
          position: "top-right",
          autoClose: 8000,
        });
      }

      onNewOrder?.(data);
    },
    [onNewOrder, playSound, showToasts],
  );

  // Order updated handler
  const handleOrderUpdated = useCallback(
    (data: OrderEventPayload) => {
      console.log("🔄 Order updated:", data);

      if (showToasts && data.status) {
        toast.info(`Order #${data.orderId}: ${data.status}`, {
          position: "top-right",
          autoClose: 4000,
        });
      }

      onOrderUpdated?.(data);
    },
    [onOrderUpdated, showToasts],
  );

  // Delivery assigned handler
  const handleDeliveryAssigned = useCallback(
    (data: OrderEventPayload) => {
      console.log("🚴 Delivery assigned:", data);

      if (playSound) {
        playNotificationSound("statusUpdate");
      }

      if (showToasts) {
        toast.success(`✅ Delivery assigned for Order #${data.orderId}`, {
          position: "top-right",
          autoClose: 5000,
        });
      }

      onDeliveryAssigned?.(data);
    },
    [onDeliveryAssigned, playSound, showToasts],
  );

  // Generic status change handler
  const handleStatusChange = useCallback(
    (data: OrderEventPayload) => {
      console.log("📊 Status change:", data);

      if (playSound) {
        playNotificationSound("statusUpdate");
      }

      onStatusChange?.(data);
    },
    [onStatusChange, playSound],
  );

  useEffect(() => {
    if (!socket || !connected) return;

    // New order events
    socket.on("order:new", handleNewOrder);
    socket.on("order:placed", handleNewOrder);

    // Order update events
    socket.on("order:updated", handleOrderUpdated);
    socket.on("order:status_change", handleOrderUpdated);

    // Delivery assignment events
    socket.on("delivery:assigned", handleDeliveryAssigned);

    // Status-specific events (for real-time updates)
    socket.on("order:accepted", handleStatusChange);
    socket.on("order:processing", handleStatusChange);
    socket.on("order:ready", handleStatusChange);
    socket.on("order:picked_up", handleStatusChange);
    socket.on("order:out_for_delivery", handleStatusChange);
    socket.on("order:delivered", handleStatusChange);
    socket.on("order:rejected", handleStatusChange);
    socket.on("order:cancelled", handleStatusChange);

    console.log("✅ Order event listeners registered");

    return () => {
      socket.off("order:new", handleNewOrder);
      socket.off("order:placed", handleNewOrder);
      socket.off("order:updated", handleOrderUpdated);
      socket.off("order:status_change", handleOrderUpdated);
      socket.off("delivery:assigned", handleDeliveryAssigned);
      socket.off("order:accepted", handleStatusChange);
      socket.off("order:processing", handleStatusChange);
      socket.off("order:ready", handleStatusChange);
      socket.off("order:picked_up", handleStatusChange);
      socket.off("order:out_for_delivery", handleStatusChange);
      socket.off("order:delivered", handleStatusChange);
      socket.off("order:rejected", handleStatusChange);
      socket.off("order:cancelled", handleStatusChange);
      console.log("🧹 Order event listeners cleaned up");
    };
  }, [
    socket,
    connected,
    handleNewOrder,
    handleOrderUpdated,
    handleDeliveryAssigned,
    handleStatusChange,
  ]);

  return { connected, playNotificationSound };
};

// Export the sound utility for external use
export { playNotificationSound };
